<template>
  <div>
    <b-card>
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <b-card-title class="mb-0">Participation</b-card-title>
          <div class="d-flex align-items-center">
            <v-select
              class="mr-1 filter-select"
              :options="companies"
              :multiple="true"
              label="label"
              v-model="filters.company"
              @input="loadData"
              placeholder="Companies"
            />
            <b-button size="sm" class="mr-1" variant="outline-primary" @click="selectAllCompanies">Select All</b-button>
            <v-select
              class="mr-1 filter-select"
              :options="courses"
              :multiple="true"
              label="label"
              v-model="filters.course"
              @input="loadData"
              placeholder="Courses"
            />
            <b-button size="sm" class="mr-1" variant="outline-primary" @click="selectAllCourses">Select All</b-button>
            <flat-pickr
              v-model="filters.dates"
              class="form-control bg-white"
              :config="datePickerConfig"
            />
          </div>
        </div>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col cols="12" lg="6" class="mb-2">
            <b-card>
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="mb-0">Attendance Rate per Event</h5>
                <b-button size="sm" variant="success" @click="doExport('exportAttendanceRatePerEvent')">Export</b-button>
              </div>
              <canvas ref="attendanceEventChart" height="220"></canvas>
            </b-card>
          </b-col>
          <b-col cols="12" lg="6" class="mb-2">
            <b-card>
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="mb-0">Participants per Company</h5>
                <b-button size="sm" variant="success" @click="doExport('exportParticipantsPerCompany')">Export</b-button>
              </div>
              <canvas ref="participantsCompanyChart" height="220"></canvas>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" lg="6" class="mb-2">
            <b-card>
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="mb-0">Course Popularity</h5>
                <b-button size="sm" variant="success" @click="doExport('exportCoursePopularity')">Export</b-button>
              </div>
              <canvas ref="coursePopularityChart" height="220"></canvas>
            </b-card>
          </b-col>
          <b-col cols="12" lg="6" class="mb-2">
            <b-card>
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="mb-0">Session Attendance </h5>
                <b-button size="sm" variant="success" @click="doExport('exportSessionAttendanceHeatmap')">Export</b-button>
              </div>
              <div class="table-responsive" style="max-height:300px; overflow:auto;">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Session Start</th><th>Event</th><th>Course</th><th>Attended</th><th>Capacity</th><th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r,idx) in sessionAttendanceHeatmap" :key="idx">
                      <td>{{ r.session_start }}</td>
                      <td>{{ r.event }}</td>
                      <td>{{ r.course }}</td>
                      <td>{{ r.attended }}</td>
                      <td>{{ r.capacity }}</td>
                      <td>{{ formatPercent(r.attendance_percent) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { onUnmounted } from '@vue/composition-api'
import store from '@/store'
import reportStoreModule from './reportStoreModule'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Chart from 'chart.js'
import vSelect from "vue-select";

export default {
  name: 'TrainingParticipation',
  components: { flatPickr,vSelect },
  data() {
    return {
      companies: [],
      courses: [],
      filters: {
        company: [],
        course: [],
        dates: [],
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        mode: 'range',
        onChange: () => this.onDateChange(),
      },
      // data sets
      attendanceRatePerEvent: [],
      participantsPerCompany: [],
      coursePopularity: [],
      sessionAttendanceHeatmap: [],
      charts: {
        attendanceEvent: null,
        participantsCompany: null,
        coursePopularity: null,
      },
    }
  },
  created() {
    const MODULE = 'trainingReports'
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, reportStoreModule)
    onUnmounted(() => { if (store.hasModule(MODULE)) store.unregisterModule(MODULE) })
    this.loadOptions().then(() => {
      this.setDefaultDates()
      this.loadData()
    })
  },
  methods: {
    setDefaultDates() {
      const end = new Date()
      const start = new Date(end)
      start.setMonth(end.getMonth() - 1)
      this.filters.dates = [start, end]
    },
    onDateChange() { this.loadData() },
    loadOptions() {
      return store.dispatch('trainingReports/getTrainingOptions').then(res => {
        this.companies = res.data.companies || []
        this.courses = res.data.courses || []
      })
    },
    selectAllCompanies() {
      this.filters.company = (this.companies || []).slice()
      this.loadData()
    },
    selectAllCourses() {
      this.filters.course = (this.courses || []).slice()
      this.loadData()
    },
    buildParams() {
      let start_date, end_date
      const d = this.filters.dates
      if (Array.isArray(d) && d.length === 2) {
        start_date = this.toYmd(d[0])
        end_date = this.toYmd(d[1])
      }
      const company_id = (this.filters.company || []).map(c => c.id).join(',') || undefined
      const course_id = (this.filters.course || []).map(c => c.id).join(',') || undefined
      return { start_date, end_date, company_id, course_id }
    },
    toYmd(x) {
      const dt = new Date(x)
      const m = (dt.getMonth()+1).toString().padStart(2,'0')
      const d = dt.getDate().toString().padStart(2,'0')
      return `${dt.getFullYear()}-${m}-${d}`
    },
    loadData() {
      const params = this.buildParams()
      store.dispatch('trainingReports/getTrainingParticipation', params).then(res => {
        const data = res.data || {}
        this.attendanceRatePerEvent = data.attendance_rate_per_event || []
        this.participantsPerCompany = data.participants_per_company || []
        this.coursePopularity = data.course_popularity || []
        this.sessionAttendanceHeatmap = data.session_attendance_heatmap || []
        this.$nextTick(() => {
          this.renderAttendanceRatePerEvent()
          this.renderParticipantsPerCompany()
          this.renderCoursePopularity()
        })
      }).catch(err => {
        this.$swal({ title: 'Error', text: 'Failed to load participation: ' + err, icon: 'error' })
      })
    },
    destroyChart(refName) {
      if (this.charts[refName]) { this.charts[refName].destroy(); this.charts[refName] = null }
    },
    renderAttendanceRatePerEvent() {
      this.destroyChart('attendanceEvent')
      const ctx = this.$refs.attendanceEventChart.getContext('2d')
      const labels = this.attendanceRatePerEvent.map(r => r.event)
      const data = this.attendanceRatePerEvent.map(r => r.attendance_percent)
      this.charts.attendanceEvent = new Chart(ctx, {
        type: 'horizontalBar',
        data: { labels, datasets: [{ label: 'Attendance %', data, backgroundColor: '#42A5F5' }] },
        options: { scales: { xAxes: [{ ticks: { callback: v => `${v}%`, beginAtZero: true, max: 100 } }] }, tooltips: { callbacks: { label: (ti, d) => `${d.datasets[0].data[ti.index]}%` } } }
      })
    },
    renderParticipantsPerCompany() {
      this.destroyChart('participantsCompany')
      const ctx = this.$refs.participantsCompanyChart.getContext('2d')
      const labels = this.participantsPerCompany.map(r => r.company)
      const data = this.participantsPerCompany.map(r => r.attendees)
      this.charts.participantsCompany = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets: [{ label: 'Attendees', data, backgroundColor: '#66BB6A' }] },
        options: { scales: { yAxes: [{ ticks: { beginAtZero: true } }] } }
      })
    },
    renderCoursePopularity() {
      this.destroyChart('coursePopularity')
      const ctx = this.$refs.coursePopularityChart.getContext('2d')
      const labels = this.coursePopularity.map(r => r.course)
      const registered = this.coursePopularity.map(r => r.registered)
      const attended = this.coursePopularity.map(r => r.attended)
      this.charts.coursePopularity = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets: [
          { label: 'Registered', data: registered, backgroundColor: '#9E9E9E' },
          { label: 'Attended', data: attended, backgroundColor: '#29B6F6' },
        ] },
        options: { scales: { yAxes: [{ ticks: { beginAtZero: true } }] } }
      })
    },
    doExport(actionName) {
      const params = this.buildParams()
      store.dispatch(`trainingReports/${actionName}`, params).then(res => {
        this.saveBlobResponse(res)
      })
    },
    saveBlobResponse(res) {
      const disposition = res.headers && res.headers['content-disposition']
      const match = disposition && disposition.match(/filename="?([^";]+)"?/)
      const filename = match ? match[1] : 'export.xlsx'
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    },
    formatPercent(v) { return v == null ? '0%' : `${Number(v).toFixed(2)}%` },
  },
}
</script>

<style scoped>
.table { font-size: 0.85rem; }
.filter-select { min-width: 260px; width: 320px; }
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
